import { CommonModule } from '@angular/common';
import { HttpContext } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  OnInit,
  OnDestroy,
  ChangeDetectorRef
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SVModule } from '@delon/abc/sv';
import { ALLOW_ANONYMOUS } from '@delon/auth';
import { _HttpClient, SettingsService } from '@delon/theme';
import { Html5Qrcode } from 'html5-qrcode';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzUploadFile, NzUploadModule } from 'ng-zorro-antd/upload';
import { Observable, Observer } from 'rxjs';
import MYStateJSON from 'src/assets/data/einvoice-my-state.json';
import OriginCountryJSON from 'src/assets/data/einvoice-origin-country.json';

@Component({
  selector: 'app-scan-taxpayer-qr',
  templateUrl: './scan-taxpayer-qr.component.html',
  styleUrls: ['./scan-taxpayer-qr.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    NzInputModule,
    NzButtonModule,
    NzIconModule,
    FormsModule,
    NzDividerModule,
    NzModalModule,
    SVModule,
    NzSpinModule,
    NzTabsModule,
    NzTableModule,
    NzTypographyModule,
    NzTagModule,
    NzAlertModule,
    NzToolTipModule,
    NzAlertModule,
    NzCardModule,
    NzUploadModule,
    NzMessageModule,
    NzSpinModule,
    NzIconModule
  ]
})
export class EInvoiceScanTaxPayerQRComponent implements OnInit, OnDestroy {
  private http = inject(_HttpClient);
  private cdr = inject(ChangeDetectorRef);
  activeTab = 0;
  visible = false;
  live = false;

  @Input() label: string = '';
  @Input() tenantId: string | null = null;
  @Input() tin: string | null = '';
  isScanning: boolean = false;
  startingScanner: boolean = false;
  countryName: string = '';
  stateName: string = '';

  // Info the api callback response
  @Output() readonly scanSucess: EventEmitter<any> = new EventEmitter();

  scanResultVisible = false;
  scanResult: any = {};
  decodedResult: string = '';
  html5QrCode: Html5Qrcode | null = null;
  isCameraAvailable: boolean = false;
  checkingCamera: boolean = false;

  constructor(
    private msg: NzMessageService,
    private settings: SettingsService
  ) {
    this.tenantId = this.settings.user['company']?.tenantId;
  }

  ngOnInit() {
    return;
  }

  initScanner() {
    if (!this.html5QrCode) {
      this.html5QrCode = new Html5Qrcode('reader');
    }
  }

  async checkCamera() {
    try {
      this.checkingCamera = true;
      const devices = await Html5Qrcode.getCameras();
      this.isCameraAvailable = devices.length > 0;
      this.checkingCamera = false;
    } catch (err) {
      this.isCameraAvailable = false;
      this.checkingCamera = false;
    }
  }

  onTabChange(event: number) {
    if (!this.html5QrCode) {
      this.initScanner();
    }
    this.decodedResult = '';
    if (event === 1 && this.html5QrCode) {
      this.startingScanner = true;
      this.html5QrCode
        .start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: { width: 220, height: 220 }
          },
          this.onScanSuccess.bind(this),
          this.onScanError.bind(this)
        )
        .then(() => {
          this.isScanning = true;
          this.startingScanner = false;
        })
        .catch(() => {
          this.startingScanner = false;
        });
    } else {
      this.stopScanner();
      this.startingScanner = false;
    }
  }

  onScanSuccess(base64: string) {
    const decodedText = atob(base64);
    this.decodedResult = decodedText;
    this.stopScanner();

    this.loadTaxPayerProfile();
  }

  onScanError(errorMessage: string) {
    // console.error(errorMessage);
  }

  stopScanner() {
    if (this.html5QrCode && this.isScanning) {
      this.html5QrCode
        .stop()
        .then(() => {
          this.isScanning = false;
        })
        .catch(() => {
          this.isScanning = false;
        });
    }
  }

  beforeUpload = (
    file: NzUploadFile,
    _fileList: NzUploadFile[]
  ): Observable<boolean> =>
    new Observable((observer: Observer<boolean>) => {
      const isLt2M = file.size! / 1024 / 1024 < 1;
      if (!isLt2M) {
        this.msg.error('Attachment must smaller than 1MB!');
        observer.complete();
        return;
      }

      if (this.html5QrCode) {
        this.html5QrCode.clear();
      } else {
        this.initScanner();
      }

      this.html5QrCode!.scanFile(file as any, false)
        .then(decodedText => {
          this.onScanSuccess(decodedText);
        })
        .catch(err => {
          console.error(err);
          this.decodedResult = 'Error decoding QR Code. Please try again.';
        });
      // observer.next(isLt2M);

      observer.complete();
    });

  ngOnDestroy() {
    this.stopScanner();
  }

  closeModal() {
    if (this.isScanning) this.stopScanner();
    this.visible = false;
  }

  async showModal() {
    this.visible = false;
    this.visible = true;
    this.decodedResult = '';

    await this.checkCamera();
    if (!this.html5QrCode) {
      // Initialize the QR code scanner after 100ms to allow the modal to render
      setTimeout(() => {
        this.initScanner();
      }, 100);
    }

    if (this.tenantId) {
      // Check if current tenant is connected to live or sandbox
      this.http
        .get(
          `api/einvoices/live`,
          { tid: this.tenantId as string },
          {
            context: new HttpContext().set(ALLOW_ANONYMOUS, true)
          }
        )
        .subscribe(resp => {
          this.live = !!resp;
        });
    }
  }

  loadTaxPayerProfile(): void {
    this.scanResult = {};
    this.http
      .get(
        `api/einvoices/TaxpayerProfile/${this.decodedResult}`,
        { tenantId: this.tenantId },
        { context: new HttpContext().set(ALLOW_ANONYMOUS, true) }
      )
      .subscribe(response => {
        this.scanResult = { ...response };
        // Map country from longCode to code
        let countryCode = response.country;
        if (response.country) {
          const country = OriginCountryJSON.find(
            c =>
              c.longCode === response.country ||
              c.code === response.country ||
              c.name === response.country
          );
          if (country) {
            countryCode = country.code;
            this.countryName = country.name.toLocaleUpperCase();
          }
        }

        if (!response.msic) {
          response.msic = '00000';
          this.scanResult.msic = '00000';
        }

        // Map country from longCode to code
        let state = response.state;
        if (response.country) {
          const state = MYStateJSON.find(
            c => c.code === response.state || c.name === response.state
          );
          if (state) {
            this.stateName = state.name;
          }
        }
        this.scanResult.state = this.stateName;
        this.scanResult.country = countryCode;

        this.showScanResult();
        // this.scanSucess.emit(response);
      });
  }

  showScanResult() {
    this.closeModal();
    this.scanResultVisible = true;
    this.cdr.detectChanges();
  }

  handleOk() {
    this.scanResultVisible = false;
    if (this.scanResult.tin) this.scanSucess.emit(this.scanResult);
  }
}
